import { RestApiClient } from 'ifield-core-components'
import log from 'loglevel'
import _ from 'lodash'
import { FUNCTION_TYPE } from '@/constants'

/**
 * センサーグループ取得
 * @param {Number} id ID
 * @returns {Array.<Object>|Object} データ
 */
const getSensorGroups = async (id) => {
  const endPoint = id == null ? 'sensor_group' : `sensor_group/${id}`
  const url = RestApiClient.createApiUrl(endPoint)
  const json = await RestApiClient.executeGetApi(url)
  return json.data
}

/**
 * 初期スロットル間隔(ms)
 */
const INITIAL_THROTTLE_TIME = 30000

/**
 * 当モジュールで扱うデータが利用可能な権限タイプ
 */
const MY_FUNCTION_TYPE = FUNCTION_TYPE.SENSOR

export default {
  namespaced: true,
  state: {
    sensorGroups: [],
    throttleTime: INITIAL_THROTTLE_TIME,
    throttler: null,
  },
  persistentStates: ['throttleTime'],
  mutations: {
    /**
     * stateを初期化する
     * @param {*} state
     */
    resetState(state) {
      state.sensorGroups = []
      state.throttleTime = INITIAL_THROTTLE_TIME
      state.throttler = null
    },
    /**
     * センサーグループを設定する
     * @param {*} state
     * @param {Array.<Object>} sensorGroups センサーグループ
     */
    setSensorGroups(state, sensorGroups) {
      state.sensorGroups = sensorGroups
    },
    /**
     * 特定のセンサーグループを設定する
     * @param {*} state
     * @param {Object} sensorGroup センサーグループ
     */
    setSpecificSensorGroup(state, sensorGroup) {
      const targetIndex = state.sensorGroups.findIndex(
        (l) => l.id === sensorGroup.id
      )
      if (targetIndex >= 0) {
        state.sensorGroups[targetIndex] = sensorGroup
      } else {
        state.sensorGroups.push(sensorGroup)
      }
    },
    /**
     * スロットル間隔を保存する
     * @param {*} state
     * @param {Number} time スロットル間隔(ms)
     */
    setThrottleTime(state, time) {
      state.throttleTime = +time
    },
    /**
     * スロットル関数を保存する
     * @param {*} state
     * @param {Function} throttler スロットル関数
     */
    setThrottler(state, throttler) {
      state.throttler = throttler
    },
  },
  getters: {
    /**
     * センサーグループ
     * @param {*} state
     * @returns {Array.<Object>}
     */
    sensorGroups: (state) => state.sensorGroups,
    /**
     * センサーグループ
     * @param {*} state
     * @returns {Map}
     */
    sensorGroupMap: (state) =>
      new Map(
        state.sensorGroups.map((sensorGroup) => [sensorGroup.id, sensorGroup])
      ),
    /**
     * 特定IDのセンサーグループを取得
     * @param {*} state
     * @param {*} getters
     * @returns {Function} IDを引数に特定IDのセンサーグループを返す関数
     */
    getSensorGroupById: (state, getters) => (id) =>
      getters.sensorGroupMap.get(id),
  },
  actions: {
    /**
     * stateをリセットする
     */
    resetState({ commit }) {
      commit('resetState')
    },
    /**
     * センサーグループを更新する
     */
    async updateSensorGroups({ commit, rootGetters }) {
      if (!rootGetters['functionType'](MY_FUNCTION_TYPE)) {
        log.info('function type: sensor is unavaliable. skip update')
        return
      }
      const sensorGroups = await getSensorGroups()
      commit('setSensorGroups', sensorGroups)
    },
    /**
     * 特定のセンサーグループを更新する
     * @param {Number} id 対象のセンサーグループID
     */
    async updateSpecificSensorGroup({ commit, rootGetters }, id) {
      if (!rootGetters['functionType'](MY_FUNCTION_TYPE)) {
        log.info('function type: sensor is unavaliable. skip update')
        return
      }
      const sensorGroup = await getSensorGroups(id)
      commit('setSpecificSensorGroup', sensorGroup)
      return sensorGroup
    },
    /**
     * センサーグループを更新する(スロットル)
     */
    async updateSensorGroupsWithThrottle({ state, commit, dispatch }) {
      if (!state.throttler) {
        const throttler = _.throttle(
          () => dispatch('updateSensorGroups'),
          state.throttleTime,
          { trailing: false }
        )
        commit('setThrottler', throttler)
      }
      return state.throttler()
    },
    /**
     * スロットル間隔更新
     * @param {Number} time スロットル間隔(ms)
     */
    updateThrottleTime({ commit }, time) {
      commit('setThrottleTime', +time)
      commit('setThrottler', null)
    },
  },
}
